import React, { useState } from "react";
import { Box, Button, Typography, Popover, Divider } from "@mui/material";
import { HexColorPicker } from "react-colorful";

/* --------------------------------------------------------------------------
 *  PRESET HEX VALUES (includes transparent sentinel)
 * ------------------------------------------------------------------------ */
const PRESET_COLORS = [
  "#D0021B", "#F5A623", "#F8E71C", "#8B572A",
  "#7ED321", "#417505", "#BD10E0", "#9013FE",
  "#4A90E2", "#50E3C2", "#B8E986", "#000000",
  "#4A4A4A", "#9B9B9B", "#FFFFFF", "transparent",
];

/* --------------------------------------------------------------------------
 *  SMALL UTILS
 * ------------------------------------------------------------------------ */
/** Regex‑based hex validator – supports #RGB and #RRGGBB (case‑insensitive) */
const isValidHex = (val) => /^#([0-9A-F]{3}){1,2}$/i.test(val);

/** Convert a *valid* hex value to its RGB components */
const hexToRgb = (hex) => {
  const cleaned = hex.replace(/^#/, "");
  const bigint = parseInt(cleaned, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

/* --------------------------------------------------------------------------
 *  COMPONENT
 * ------------------------------------------------------------------------ */
const ColorPicker = ({
  color,
  label,
  onChange,
  anchorEl,
  handleClose,
  skipButton = false,
  colorKey = "fontColor", // Determines which key parent uses
}) => {
  /* ──────────────────────────── State ───────────────────────────── */
  const [customColor, setCustomColor] = useState(color || "#000000");
  const [rgba, setRgba] = useState({ r: 0, g: 0, b: 0, a: 1 });
  const [internalAnchor, setInternalAnchor] = useState(null); // anchor for standalone mode

  /* ───────────────────── Internal click helpers ─────────────────── */
  const handleClick = (e) => setInternalAnchor(e.currentTarget);
  const handleInternalClose = () => setInternalAnchor(null);

  /* ------------------------------------------------------------------
   *  Hex handler – LET THE USER TYPE FREELY, validate afterwards.
   * ---------------------------------------------------------------- */
  const handleHexChange = (inputValue) => {
    // 1. Reflect *exactly* what the user typed so the field is editable
    setCustomColor(inputValue);

    // 2. Normalise (add "#" if user pasted without it)
    const normalised = inputValue.startsWith("#") ? inputValue : `#${inputValue}`;

    // 3. Only commit if full, valid hex
    if (isValidHex(normalised)) {
      const rgbValues = hexToRgb(normalised);
      setRgba({ ...rgbValues, a: rgba.a });
      console.log(`[ColorPicker] Selected color: ${normalised}`); // 🔎  Debugging log
      onChange(normalised); // propagate to parent
    }
  };

  /** Extra safety: onBlur, revert to last known good colour if the field is invalid */
  const handleHexBlur = () => {
    if (!isValidHex(customColor)) {
      setCustomColor(color || "#000000");
    }
  };

  /* ------------------------------------------------------------------
   *  RGB handler – unchanged; always persists then propagates rgba string
   * ---------------------------------------------------------------- */
  const handleRgbChange = (channel, value) => {
    const newRgba = { ...rgba, [channel]: Number(value) };
    setRgba(newRgba);
    // Keep customColor in sync so the button / picker reflect the new value
    setCustomColor(`rgba(${newRgba.r}, ${newRgba.g}, ${newRgba.b}, ${newRgba.a})`);
    onChange(`rgba(${newRgba.r}, ${newRgba.g}, ${newRgba.b}, ${newRgba.a})`);
  };

  /* ------------------------------------------------------------------
   *  Preset swatch click
   * ---------------------------------------------------------------- */
  const handlePresetClick = (preset) => {
    const presetValue = preset === "transparent" ? "rgba(0, 0, 0, 0)" : preset;
    setCustomColor(presetValue);
    onChange(presetValue);
  };

  /* ------------------------------------------------------------------
   *  Popover
   * ---------------------------------------------------------------- */
  const open = Boolean(skipButton ? anchorEl : internalAnchor);
  const id = open ? "simple-popover" : undefined;

  /* ------------------------------------------------------------------
   *  Render
   * ---------------------------------------------------------------- */
  return (
    <Box sx={{ textAlign: "center", width: "100%", p: 0 }}>
      {/* Label */}
      <Typography variant="caption" display="block" gutterBottom>
        {label}
      </Typography>

      {/* Colour swatch button (hidden when used as toolbar popover) */}
      {!skipButton && (
        <Button
          onClick={handleClick}
          sx={{
            width: "90%",
            height: 35,
            backgroundColor:
              color === "transparent" ? "rgba(0,0,0,0.1)" : color,
            border: "1px solid rgba(0, 0, 0, 0.2)",
            "&:hover": {
              backgroundColor:
                color === "transparent" ? "rgba(0,0,0,0.1)" : color,
              opacity: 0.9,
            },
          }}
        />
      )}

      {/* Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={skipButton ? anchorEl : internalAnchor}
        onClose={skipButton ? handleClose : handleInternalClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          {/* React‑Colorful picker */}
          <HexColorPicker
            color={customColor}
            onChange={handleHexChange}
            style={{ width: "100%", height: 150, marginBottom: 5 }}
          />

          {/* Manual input section */}
          <Box sx={{ display: "flex", gap: 0, mt: 0 }}>
            {["Hex", "R", "G", "B", "A"].map((lbl) => (
              <Box
                key={lbl}
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  p: 0.5,
                }}
              >
                <input
                  type={lbl === "A" ? "number" : "text"}
                  maxLength={lbl === "Hex" ? 7 : undefined}
                  step={lbl === "A" ? 0.05 : undefined}
                  value={
                    lbl === "Hex" ? customColor : rgba[lbl.toLowerCase()] || ""
                  }
                  onChange={(e) =>
                    lbl === "Hex"
                      ? handleHexChange(e.target.value)
                      : handleRgbChange(lbl.toLowerCase(), e.target.value)
                  }
                  onBlur={lbl === "Hex" ? handleHexBlur : undefined}
                  placeholder={lbl}
                  style={{
                    width: lbl === "Hex" ? 55 : 27,
                    fontSize: 11,
                    textAlign: "center",
                    padding: 2,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: 10, color: "#666" }}>
                  {lbl}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Preset swatches */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 0.5 }}>
            {PRESET_COLORS.map((preset) => (
              <Box
                key={preset}
                sx={{
                  width: 18,
                  height: 18,
                  backgroundColor: preset === "transparent" ? "white" : preset,
                  border: "1px solid rgba(0,0,0,0.2)",
                  position: "relative",
                  cursor: "pointer",
                  "&:hover": { borderColor: "#000" },
                }}
                onClick={() => handlePresetClick(preset)}
              >
                {preset === "transparent" && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "repeating-conic-gradient(#eee 0% 25%, #ddd 0% 50%) 50% / 8px 8px",
                      clipPath: "polygon(0% 0%, 100% 100%, 100% 0%)",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default ColorPicker;
